import React, { useMemo, useState } from 'react';
import type { Sucursal } from '../types';
import { PageShell, glassLight, primaryButton, ghostButton, dangerButton, Badge, EmptyState, MotionGrid, MotionItem, useIsMobile, usePagination, Pagination, T, Modal } from '../ui';
export function Sucursales({
  sucs,
  onNew,
  onEdit,
  onDelete,
  canArchive
}: {
  sucs: Sucursal[];
  onNew?: () => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  canArchive?: boolean;
}) {
  const [tab, setTab] = useState<'activos' | 'archivados'>('activos');
  const [confirm, setConfirm] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const filteredSucs = useMemo(() => {
    return sucs.filter(s => tab === 'activos' ? !s.archivado : s.archivado);
  }, [sucs, tab]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filteredSucs, 6);
  return <PageShell title="Sucursales" sub={`${filteredSucs.length} puntos comerciales`} action={onNew ? <button onClick={onNew} style={primaryButton} data-id="element-564">
            + Nueva sucursal
          </button> : undefined} data-id="element-563">
      <div style={{
      display: 'flex',
      gap: 8,
      marginBottom: 20
    }} data-id="element-565">
        <button onClick={() => {
        setTab('activos');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'activos' ? T.accentSoft : 'transparent',
        color: tab === 'activos' ? T.accent : T.textMuted
      }} data-id="element-566">
          Activos
        </button>
        {canArchive && <button onClick={() => {
        setTab('archivados');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'archivados' ? T.accentSoft : 'transparent',
        color: tab === 'archivados' ? T.accent : T.textMuted
      }} data-id="element-567">
            Archivados
          </button>}
      </div>
      <style data-id="element-568">{`
        .suc-card { transition: transform .16s ease, box-shadow .16s ease, border-color .16s ease; }
        @media (min-width: 768px) {
          .suc-card:hover { transform: translateY(-3px); box-shadow: 0 14px 36px rgba(2,6,23,.45); }
        }
      `}</style>
      {sucs.length === 0 ? <EmptyState title="Aún no hay sucursales" hint="Crea tu primer punto comercial para empezar a registrar consumo." data-id="element-569" /> : <MotionGrid style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(340px,1fr))',
      gap: 16
    }} data-id="element-570">
          {pageItems.map(s => <MotionItem key={s.id} className="getr-card suc-card" style={{
        ...glassLight,
        padding: isMobile ? 20 : 22,
        display: 'flex',
        flexDirection: 'column'
      }} data-id="element-571">
              <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          gap: 12,
          flex: 1
        }} data-id="element-572">
                <div style={{
            display: 'flex',
            gap: 13,
            minWidth: 0
          }} data-id="element-573">
                  <div style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              flexShrink: 0,
              background: T.accentSoft,
              border: `1px solid ${T.accentBorder}`,
              color: T.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }} data-id="element-574">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" data-id="element-575">
                      <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4" data-id="element-576" />
                      <path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01" data-id="element-577" />
                    </svg>
                  </div>
                  <div style={{
              minWidth: 0
            }} data-id="element-578">
                    <div style={{
                fontWeight: 700,
                color: T.text,
                fontSize: 15
              }} data-id="element-579">
                      {s.nombre}
                    </div>
                    <div style={{
                marginTop: 6
              }} data-id="element-580">
                      <Badge label={s.tipo} color={T.info} soft={T.infoSoft} dot={false} data-id="element-581" />
                    </div>
                    <div style={{
                fontSize: 12,
                color: T.textMuted,
                marginTop: 8
              }} data-id="element-582">
                      {s.direccion}
                    </div>
                  </div>
                </div>
                {(onEdit || onDelete) && <div style={{
            display: 'flex',
            gap: 8,
            flexShrink: 0,
            width: isMobile ? '100%' : 'auto',
            marginTop: isMobile ? 4 : 0,
            paddingTop: isMobile ? 12 : 0,
            borderTop: isMobile ? `1px solid ${T.border}` : 'none'
          }} data-id="element-583">
                    {onEdit && <button className="getr-btn-ghost" onClick={() => onEdit(s.id)} style={{
              ...ghostButton,
              flex: isMobile ? 1 : 'none'
            }} data-id="element-584">
                        Editar
                      </button>}
                    {onDelete && <button onClick={() => setConfirm(s.id)} style={{
              ...ghostButton,
              flex: isMobile ? 1 : 'none',
              color: s.archivado ? T.info : T.warn,
              borderColor: s.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
              background: s.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
            }} data-id="element-585">
                        {s.archivado ? 'Desarchivar' : 'Archivar'}
                      </button>}
                  </div>}
              </div>
            </MotionItem>)}
        </MotionGrid>}
      {sucs.length > 0 && <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="sucursales" data-id="element-586" />}

      {confirm && <Modal title="Confirmar acción" onClose={() => setConfirm(null)} data-id="element-587">
          <div style={{
        color: T.textSecondary,
        marginBottom: 24,
        fontSize: 14,
        lineHeight: 1.5
      }} data-id="element-588">
            ¿Estás seguro de que deseas{' '}
            {sucs.find(s => s.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}{' '}
            este punto comercial?
          </div>
          <div style={{
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end'
      }} data-id="element-589">
            <button onClick={() => setConfirm(null)} style={{
          ...ghostButton,
          padding: '10px 20px'
        }} data-id="element-590">
              Cancelar
            </button>
            <button onClick={() => {
          if (onDelete) onDelete(confirm);
          setConfirm(null);
        }} style={{
          ...primaryButton,
          background: sucs.find(s => s.id === confirm)?.archivado ? T.info : T.warn,
          borderColor: sucs.find(s => s.id === confirm)?.archivado ? T.info : T.warn,
          color: '#0B1120'
        }} data-id="element-591">
              Sí,{' '}
              {sucs.find(s => s.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}
            </button>
          </div>
        </Modal>}
    </PageShell>;
}